import { useState, useEffect } from "react";
import api from "@/services/api";
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Shield,
  User,
  Mail,
  AlertTriangle,
  Database,
  Radar,
  RefreshCw,
  Sparkles,
  Cpu,
  DollarSign,
  Zap,
  BarChart3,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Admin({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: "", name: "", role: "user" });
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [leadCount, setLeadCount] = useState(0);
  const [spotterRunning, setSpotterRunning] = useState(false);
  const [tokenStats, setTokenStats] = useState(null);
  const [loadingTokens, setLoadingTokens] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchLeadCount();
    fetchTokenStats();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadCount = async () => {
    try {
      const response = await api.get('/leads');
      setLeadCount(response.data.length);
    } catch (error) {
      console.error("Error fetching lead count:", error);
      toast.error("Error cargando conteo de leads");
    }
  };

  const fetchTokenStats = async () => {
    setLoadingTokens(true);
    try {
      const response = await api.get('/analyzer/ai-token-stats');
      setTokenStats(response.data);
    } catch (error) {
      console.error("Error fetching token stats:", error);
      toast.error("Error cargando estadísticas de tokens");
    } finally {
      setLoadingTokens(false);
    }
  };

  const openModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setFormData({
        email: userToEdit.email,
        name: userToEdit.name,
        role: userToEdit.role,
      });
    } else {
      setEditingUser(null);
      setFormData({ email: "", name: "", role: "user" });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormData({ email: "", name: "", role: "user" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        await api.put(
          `/users/${editingUser.user_id}`,
          { name: formData.name, role: formData.role }
        );
        toast.success("Usuario actualizado");
      } else {
        await api.post('/users', formData);
        toast.success("Usuario creado");
      }
      closeModal();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar usuario");
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`¿Eliminar a ${userName}?`)) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`¿Resetear la contraseña de ${userName} a la default (Srs2026!)?`)) return;

    try {
      await api.post(`/users/${userId}/reset-password`, {});
      toast.success(`Contraseña de ${userName} reseteada a Srs2026!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al resetear contraseña");
    }
  };

  const handleDeleteAllLeads = async () => {
    if (confirmDeleteText !== "ELIMINAR") {
      toast.error("Por favor escribe 'ELIMINAR' para confirmar");
      return;
    }

    try {
      const response = await api.delete('/leads/delete-all');
      toast.success(`${response.data.deleted} leads eliminados`);
      setDeleteAllDialogOpen(false);
      setConfirmDeleteText("");
      fetchLeadCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar leads");
    }
  };

  const handleResetMock = async () => {
    if (!window.confirm("¿Resetear datos mock? Esto eliminará TODAS las oportunidades actuales.")) return;
    try {
      await api.post('/oportunidades/reset-mock', {});
      toast.success("Datos mock reseteados");
    } catch (error) {
      toast.error("Error al resetear datos");
    }
  };

  const handleRunSpotter = async () => {
    setSpotterRunning(true);
    try {
      const response = await api.post(
        '/spotter/run',
        { use_mock: true }
      );
      const { stats: runStats } = response.data;
      toast.success(
        `Spotter mock: ${runStats.nuevas} nuevas, ${runStats.actualizadas} actualizadas, ${runStats.descartadas} descartadas`
      );
    } catch (error) {
      toast.error("Error al ejecutar Spotter");
    } finally {
      setSpotterRunning(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-red-400 opacity-50" />
          <h2 className="text-xl font-semibold theme-text mb-2">Acceso Denegado</h2>
          <p className="theme-text-secondary">Solo administradores pueden acceder a esta sección.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cyan-400 animate-pulse">Cargando usuarios...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display theme-text flex items-center gap-2">
            <Shield className="w-6 h-6 text-teal-400" />
            Administración
          </h1>
          <p className="theme-text-secondary text-sm">Gestión de usuarios del CRM</p>
        </div>
        <Button
          onClick={() => openModal()}
          data-testid="new-user-btn"
          className="btn-gradient"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Users List */}
      <Card className="theme-bg-secondary border theme-border">
        <CardHeader>
          <CardTitle className="text-lg font-semibold theme-text flex items-center gap-2">
            <Users className="w-5 h-5" />
            Usuarios ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8 theme-text-muted">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay usuarios registrados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((u) => (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between p-4 rounded-lg theme-bg-tertiary border theme-border"
                  data-testid={`user-row-${u.user_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-semibold">
                      {u.name?.charAt(0) || "U"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium theme-text">{u.name}</span>
                        <Badge className={u.role === "admin" ? "bg-cyan-500 text-white" : "bg-slate-600 text-white"}>
                          {u.role === "admin" ? "Admin" : "Usuario"}
                        </Badge>
                        {u.user_id === user.user_id && (
                          <Badge variant="outline" className="border-cyan-400 text-cyan-400">
                            Tú
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm theme-text-secondary flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {u.email}
                      </p>
                    </div>
                  </div>
                  
                  {u.user_id !== user.user_id && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleResetPassword(u.user_id, u.name)}
                        className="theme-text-secondary hover:text-amber-400"
                        title="Resetear contraseña"
                      >
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openModal(u)}
                        className="theme-text-secondary hover:text-cyan-400"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(u.user_id, u.name)}
                        className="theme-text-secondary hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Token Consumption */}
      <Card className="theme-bg-secondary border theme-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold theme-text flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Consumo IA — Tokens
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchTokenStats}
              disabled={loadingTokens}
              className="theme-text-secondary hover:text-cyan-400"
            >
              <RefreshCw className={`w-4 h-4 ${loadingTokens ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingTokens && !tokenStats ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-purple-400" />
              <p className="mt-2 theme-text-muted text-sm">Cargando estadisticas...</p>
            </div>
          ) : tokenStats ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
                  <Zap className="w-6 h-6 mx-auto text-purple-400 mb-2" />
                  <p className="text-2xl font-bold text-purple-400">
                    {(tokenStats.total_tokens || 0).toLocaleString("es-ES")}
                  </p>
                  <p className="text-xs theme-text-muted uppercase tracking-wide mt-1">Tokens Totales</p>
                </div>
                <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-center">
                  <BarChart3 className="w-6 h-6 mx-auto text-cyan-400 mb-2" />
                  <p className="text-2xl font-bold text-cyan-400">
                    {tokenStats.total_calls || 0}
                  </p>
                  <p className="text-xs theme-text-muted uppercase tracking-wide mt-1">Llamadas IA</p>
                </div>
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                  <DollarSign className="w-6 h-6 mx-auto text-green-400 mb-2" />
                  <p className="text-2xl font-bold text-green-400">
                    ${tokenStats.estimated_cost_usd?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-xs theme-text-muted uppercase tracking-wide mt-1">Coste Estimado</p>
                </div>
              </div>

              {/* By Provider */}
              {tokenStats.by_provider && Object.keys(tokenStats.by_provider).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-purple-400" />
                    Por Proveedor
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(tokenStats.by_provider)
                      .sort(([, a], [, b]) => b.tokens - a.tokens)
                      .map(([provider, data]) => {
                        const pct = tokenStats.total_tokens > 0
                          ? Math.round((data.tokens / tokenStats.total_tokens) * 100)
                          : 0;
                        const providerColors = {
                          gemini: { bar: "bg-blue-500", text: "text-blue-400", label: "Gemini" },
                          openai: { bar: "bg-green-500", text: "text-green-400", label: "OpenAI" },
                          anthropic: { bar: "bg-orange-500", text: "text-orange-400", label: "Anthropic" },
                        };
                        const cfg = providerColors[provider] || { bar: "bg-slate-500", text: "text-slate-400", label: provider };

                        return (
                          <div key={provider} className="p-3 rounded-lg theme-bg-tertiary border theme-border">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`font-semibold ${cfg.text}`}>{cfg.label}</span>
                                {data.models && Object.keys(data.models).map((model) => (
                                  <Badge key={model} variant="outline" className="text-[10px] border-slate-600 theme-text-muted">
                                    {model}
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex items-center gap-3 text-sm">
                                <span className="theme-text-muted">{data.calls} calls</span>
                                <span className={`font-bold ${cfg.text}`}>
                                  {data.tokens.toLocaleString("es-ES")} tokens
                                </span>
                                <span className="text-green-400 text-xs">
                                  ${data.estimated_cost_usd?.toFixed(4) || "0"}
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-slate-700/30 rounded-full h-2">
                              <div
                                className={`${cfg.bar} h-2 rounded-full transition-all`}
                                style={{ width: `${Math.max(pct, 2)}%` }}
                              />
                            </div>
                            <p className="text-[10px] theme-text-muted mt-1 text-right">{pct}%</p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* By Analysis Type */}
              {tokenStats.by_type && Object.keys(tokenStats.by_type).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold theme-text mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-cyan-400" />
                    Por Tipo de Analisis
                  </h4>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(tokenStats.by_type)
                      .sort(([, a], [, b]) => b.tokens - a.tokens)
                      .map(([type, data]) => {
                        const typeLabels = {
                          pain_analysis: { label: "Pain Analysis", icon: "🩺" },
                          pliego_analysis: { label: "Analisis Pliego", icon: "📄" },
                          relevance_analysis: { label: "Relevancia IA", icon: "🎯" },
                          company_enrichment: { label: "Enriquecer Empresa", icon: "🏢" },
                          licitacion_analysis: { label: "Analisis Licitacion", icon: "📋" },
                        };
                        const cfg = typeLabels[type] || { label: type, icon: "🤖" };

                        return (
                          <div key={type} className="p-3 rounded-lg theme-bg-tertiary border theme-border">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">{cfg.icon}</span>
                              <span className="text-xs font-medium theme-text">{cfg.label}</span>
                            </div>
                            <p className="text-lg font-bold text-purple-400">
                              {data.tokens.toLocaleString("es-ES")}
                            </p>
                            <p className="text-[10px] theme-text-muted">
                              {data.calls} llamadas
                            </p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Configured Providers */}
              {tokenStats.providers_configured && tokenStats.providers_configured.length > 0 && (
                <div className="p-3 rounded-lg theme-bg-tertiary border theme-border">
                  <p className="text-xs theme-text-muted uppercase tracking-wide mb-2">Proveedores Configurados</p>
                  <div className="flex flex-wrap gap-2">
                    {tokenStats.providers_configured.map((p) => (
                      <Badge key={p.name} className="bg-green-500/20 text-green-400 border-0">
                        {p.name}: {p.model}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 theme-text-muted">
              <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay datos de consumo IA disponibles</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-500/30 bg-red-500/5">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Zona Peligrosa
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-4 rounded-lg border border-red-500/30 bg-red-500/5">
            <div>
              <h4 className="font-medium text-slate-200">Eliminar todos los leads</h4>
              <p className="text-sm text-slate-400">
                Esta acción eliminará permanentemente {leadCount} leads y no se puede deshacer.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setDeleteAllDialogOpen(true)}
              disabled={leadCount === 0}
              className="border-red-500/50 text-red-400 hover:bg-red-500/20 hover:border-red-500"
              data-testid="delete-all-leads-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              Borrar todos los leads
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg border border-red-500/30 bg-red-500/5">
            <div>
              <h4 className="font-medium text-slate-200">Reset Mock Oportunidades</h4>
              <p className="text-sm text-slate-400">
                Elimina TODAS las oportunidades y las reemplaza con datos ficticios.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleResetMock}
              className="border-red-500/50 text-red-400 hover:bg-red-500/20 hover:border-red-500"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset Mock
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg border border-orange-500/30 bg-orange-500/5">
            <div>
              <h4 className="font-medium text-slate-200">Ejecutar Spotter (Mock)</h4>
              <p className="text-sm text-slate-400">
                Ejecuta el spotter con datos ficticios. Insertará oportunidades de prueba.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleRunSpotter}
              disabled={spotterRunning}
              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20 hover:border-orange-500"
            >
              <Radar className={`w-4 h-4 mr-2 ${spotterRunning ? "animate-spin" : ""}`} />
              {spotterRunning ? "Ejecutando..." : "Ejecutar Spotter"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Modal */}
      <Dialog open={modalOpen} onOpenChange={closeModal}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <User className="w-5 h-5 text-cyan-400" />
              {editingUser ? "Editar Usuario" : "Nuevo Usuario"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                required
                disabled={!!editingUser}
                data-testid="user-email"
                className="bg-slate-950 border-slate-800 focus:border-cyan-400 disabled:opacity-50"
                placeholder="usuario@systemrapidsolutions.com"
              />
              {!editingUser && (
                <p className="text-xs text-slate-500">
                  El usuario podrá acceder con este email de Google
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                Nombre
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
                data-testid="user-name"
                className="bg-slate-950 border-slate-800 focus:border-cyan-400"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Rol
              </Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
              >
                <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="user">Usuario</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Los administradores pueden gestionar usuarios y configuración
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="border-white/10 text-slate-300 hover:bg-slate-800"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="user-submit"
                className="btn-gradient"
              >
                {editingUser ? "Guardar Cambios" : "Crear Usuario"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete All Leads Dialog */}
      <AlertDialog open={deleteAllDialogOpen} onOpenChange={(open) => {
        setDeleteAllDialogOpen(open);
        if (!open) setConfirmDeleteText("");
      }}>
        <AlertDialogContent className="bg-slate-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              ¿Estás seguro?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 space-y-4">
              <p className="text-red-400 font-medium">
                Esta acción eliminará TODOS los leads permanentemente.
              </p>
              <p>
                Se eliminarán <strong className="text-white">{leadCount} leads</strong> y todo su historial de actividades.
                Esta acción NO se puede deshacer.
              </p>
              <div className="pt-2">
                <p className="text-slate-300 mb-2">
                  Para confirmar, escribe <strong className="text-red-400">ELIMINAR</strong>:
                </p>
                <Input
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value.toUpperCase())}
                  placeholder="ELIMINAR"
                  className="bg-slate-950 border-slate-800 text-center font-mono"
                  autoComplete="off"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-slate-300 hover:bg-slate-800">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllLeads}
              disabled={confirmDeleteText !== "ELIMINAR"}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar todos los leads
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
